import React, { useMemo } from "react";
import { Link, type BreadcrumbItem, type BreadcrumbProps } from "@sumprio/ui";
import { SlashIcon } from "@sumprio/ui/icons";
import { SafeTooltipTrigger as TooltipTrigger } from "components/common/SafeTooltipTrigger";
import { truncateDisplayTextWords } from "utils/truncateDisplayText";

export interface TruncatedBreadcrumbProps extends Omit<BreadcrumbProps, "items"> {
  items: BreadcrumbItem[];
  maxWords?: number;
}

function BreadcrumbLink({
  item,
  displayText,
  fullText,
  truncated,
  isLast,
}: {
  item: BreadcrumbItem;
  displayText: string;
  fullText: string;
  truncated: boolean;
  isLast: boolean;
}) {
  const link = (
    <Link
      text={displayText}
      href={item.href}
      onClick={item.onClick}
      size="xs"
      disabled={isLast}
      className="breadcrumb__link"
      aria-label={truncated ? fullText : undefined}
    />
  );

  if (!truncated) return link;

  return (
    <TooltipTrigger
      tooltip={fullText}
      placement="bottom-start"
      theme="dark"
      maxWidth="min(420px, 92vw)"
    >
      <span style={{ display: "inline-flex", minWidth: 0, maxWidth: "100%" }}>{link}</span>
    </TooltipTrigger>
  );
}

export default function TruncatedBreadcrumb({
  items,
  maxWords = 4,
  separatorIcon: SeparatorIcon = SlashIcon,
  className,
  style,
  ...props
}: TruncatedBreadcrumbProps) {
  const previews = useMemo(
    () => items.map((item) => truncateDisplayTextWords(item.text, maxWords)),
    [items, maxWords]
  );

  if (!items.length) return null;

  return (
    <nav
      className={["breadcrumb", className].filter(Boolean).join(" ")}
      style={{ minWidth: 0, maxWidth: "100%", ...style }}
      aria-label="Breadcrumb"
      {...props}
    >
      <ol className="breadcrumb__list">
        {items.map((item, index) => {
          const preview = previews[index];
          const isLast = index === items.length - 1;
          return (
            <li key={`${index}-${preview.display}`} className="breadcrumb__item">
              <BreadcrumbLink
                item={item}
                displayText={preview.display}
                fullText={preview.full}
                truncated={preview.truncated}
                isLast={isLast}
              />
              {!isLast && (
                <span className="breadcrumb__separator" aria-hidden="true">
                  <SeparatorIcon size="xs" />
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
